<template>
    <div class="row">
        <div class="d-flex justify-content-end align-items-center flex-wrap">
            <div class="block-options content-heading">
                <select class="btn-block-option" v-model="perPage" @change="updatePerPage">
                    <option value="10">10</option>
                    <option value="20">20</option>
                    <option value="50">50</option>
                    <option value="100">100</option>
                    <option value="200">200</option>
                </select>
            </div>
        </div>
    </div>

    <!-- Reports Table -->
    <div class="block-rounded block">
        <div class="block-header block-header-default d-flex justify-content-between align-items-center">
            <div>
                <h3 class="block-title m-0 d-flex align-items-center justify-content-between flex-wrap">
                    <div>
                        {{ $t('title_result') }} <span>{{ reports.length }}</span>
                        <span v-if="selectedReports.length > 0" class="text-muted ms-2">
                            ( {{ $t('title_selected') }} {{ selectedReports.length }} )
                        </span>
                    </div>
                    <div v-if="selectedReports.length > 0" class="btn-group mt-2 mt-md-0 ms-md-3" role="group">
                        <button v-if="selectedReports.length !== reports.length" type="button"
                            class="btn btn-sm btn-alt-primary" @click="toggleSelectAll">
                            <i class="fa-solid fa-list-check me-1"></i> Select All
                        </button>

                        <button v-else type="button" class="btn btn-sm btn-alt-secondary" @click="deselectAll">
                            <i class="fa-solid fa-xmark me-1"></i> Clear All
                        </button>
                    </div>

                </h3>

            </div>
        </div>

        <div class="block-content block-content-full">
            <div class="table-responsive">
                <table class="table-sm table-vcenter fs-sm table-hover table" style="table-layout: auto; width: 100%">
                    <thead>
                        <tr class="text-center fs-6">
                            <th style="width: 90px">{{ $t('table_reference') }}</th>
                            <th style="width: 100px">{{ $t('table_merchant') }}</th>
                            <th style="width: 100px">{{ $t('table_amount') }}</th>
                            <th style="width: 80px">{{ $t('table_status') }}</th>
                            <th style="width: 100px">{{ $t('table_issued_by') }}</th>
                            <th style="width: 120px">{{ $t('table_issued_at') }}</th>
                            <th style="width: 20px"></th>
                        </tr>
                        <tr>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search " />
                                    <InputText v-model="filters.referenceSearch" type="text" size="small"
                                        class="w-100 h-9" />
                                </IconField>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.courierFilter" :options="users" optionLabel="first_name"
                                    optionValue="_id" :placeholder="$t('placeholder.any')" filter :maxSelectedLabels="3"
                                    class="w-full text-sm h-9" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center text-sm gap-2">
                                            <img :alt="slotProps.option.name" :src=slotProps.option.avatar
                                                class="w-8 h-8 rounded-full object-cover" style="width: 32px" />
                                            <span>{{ slotProps.option.first_name }} {{ slotProps.option.last_name
                                            }}</span>
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <IconField>
                                    <InputIcon class="pi pi-search" />
                                    <InputText v-model="filters.reportValueSearch" type="text" size="small"
                                        class="w-100 h-9" />
                                </IconField>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.statusFilter" :options="statusOptions" optionLabel="label"
                                    optionValue="value" :placeholder="$t('placeholder.any')"
                                    class="w-full text-sm w-100 h-9" :maxSelectedLabels="1" :selectionLimit="1">
                                    <template #value="slotProps">
                                        <span v-if="slotProps.value && slotProps.value.length > 0">
                                            <i :class="statusIconClass(slotProps.value[0])" class="mr-2"></i>
                                            {{ statusLabel(slotProps.value[0]) }}
                                        </span>
                                        <span v-else>
                                            {{ $t('placeholder.any') }}
                                        </span>
                                    </template>
                                    <template #option="slotProps">
                                        <div class="flex items-center gap-2">
                                            <i :class="statusIconClass(slotProps.option.value)" class="mr-2"></i>
                                            <span>{{ slotProps.option.label }}</span>
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <MultiSelect v-model="filters.issuedByFilter" :options="users" optionLabel="first_name"
                                    optionValue="_id" :placeholder="$t('placeholder.any')" filter :maxSelectedLabels="3"
                                    class="w-full text-sm h-9" :selectionLimit="null">
                                    <template #option="slotProps">
                                        <div class="text-sm flex items-center text-sm gap-2">
                                            <img :alt="slotProps.option.name" :src=slotProps.option.avatar
                                                class="w-8 h-8 rounded-full object-cover" style="width: 32px" />
                                            <span>{{ slotProps.option.first_name }} {{ slotProps.option.last_name
                                            }}</span>
                                        </div>
                                    </template>
                                </MultiSelect>
                            </th>
                            <th>
                                <DatePicker v-model="filters.createdAtSearch" showIcon fluid iconDisplay="input"
                                    class="h-9" size="small" />
                            </th>
                            <th class="text-center align-middle">
                                <button type="button" class="btn btn-alt-secondary btn-sm" title="Clear Filters"
                                    @click="clearFilters">
                                    <i class="fa fa-solid fa-trash"></i>
                                </button>
                            </th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="(report, index) in paginatedReports" :key="report._id" class="text-center" :class="[
                            isSelected(report) ? 'table-primary selected-row' : ''
                        ]" @click="selectReport(report, index, $event)">
                            <td class="text-truncate" style="max-width: 90px">
                                <a :href="`/partner/report/details/view/${report._id}`"
                                    @click.prevent="navigateTo(report._id)" @click.stop>
                                    {{ report.reference }}
                                </a>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">
                                <template v-if="report.merchant">
                                    <a :href="`/profile/${report.user_id}`" @click.stop>
                                        {{ report.merchant.first_name }} {{ report.merchant.last_name }}
                                    </a>
                                </template>
                                <span v-else class="text-danger">No Client Data</span>
                            </td>

                            <td class="text-truncate" style="max-width: 100px">{{
                                formatNumber(report.total_shipments_value) }}
                                {{ defaultCurrencyAcronym }}</td>
                            <td>
                                <span class="fs-xs fw-semibold d-inline-block py-1 px-3 rounded-pill" :class="{
                                    'bg-success-light text-success': report.is_settled,
                                    'bg-warning-light text-warning': !report.is_settled
                                }">
                                    {{ report.is_settled ? 'Approved' : 'Pending' }}
                                </span>
                            </td>
                            <td class="text-truncate" style="max-width: 100px">
                                <template v-if="report.creator?.first_name">
                                    <a :href="`/profile/${report.created_by}`" @click.stop>
                                        {{ report.creator.first_name }} {{
                                            report.creator.last_name }}
                                    </a>
                                </template>
                                <span v-else class="badge bg-info">Unassigned</span>
                            </td>
                            <td class="text-truncate" style="max-width: 90px">{{ formatDate(report.created_at) }}
                            </td>
                            <td>

                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
        </div>
    </div>

    <nav aria-label="Page navigation">
        <ul class="pagination justify-content-end push">
            <li class="page-item" :class="{ disabled: currentPage === 1 }">
                <a class="page-link" href="#" @click.prevent="prevPage">
                    <i class="fa fa-angle-left"></i>
                </a>
            </li>
            <li v-for="page in pages" :key="page" class="page-item" :class="{ active: page === currentPage }">
                <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
            </li>
            <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                <a class="page-link" href="#" @click.prevent="nextPage">
                    <i class="fa fa-angle-right"></i>
                </a>
            </li>
        </ul>
    </nav>
</template>
<script>
import MultiSelect from 'primevue/multiselect';
import DatePicker from 'primevue/datepicker';

function debounce(fn, delay) {
    let timeout;
    return function (...args) {
        clearTimeout(timeout);
        timeout = setTimeout(() => fn.apply(this, args), delay);
    };
}

export default {
    props: {
        reports: {
            type: Array,
            default: () => []
        },
        initialSelectedReports: {
            type: Array,
            default: () => []
        },
    },

    data() {
        return {
            loading: true,
            perPage: 50,
            currentPage: 1,
            selectAll: false,
            selectedReports: [], // Local state for selected reports
            lastSelectedIndex: null, // for shift-click range
            filters: {
                referenceSearch: null,
                courierFilter: [],
                merchantFilter: [],
                reportValueSearch: null,
                noteSearch: null,
                issuedByFilter: [],
                createdAtSearch: null
            },
            statusOptions: [
                { label: 'Settled', value: 'settled' },
                { label: 'Pending', value: 'pending' }
            ],
            users: [],
            isExporting: false,
            printing: false
        }
    },

    mounted() {
        this.fetchUsers();
    },

    watch: {
        filters: {
            handler: debounce(function () {
                this.$emit('filters-updated', {
                    ...(this.filters || {}),
                    page: this.currentPage,
                    perPage: this.perPage
                });
            }, 500), // 500ms delay
            deep: true,
            immediate: false
        },
        initialSelectedReports: {
            handler(newVal) {
                // Sync parent's selection with local state
                this.selectedReports = [...newVal];
            },
            immediate: true
        },
    },

    computed: {
        selectedReportIds() {
            return this.selectedReports;
        },

        paginatedReports() {
            const start = (this.currentPage - 1) * this.perPage;
            const end = start + this.perPage;
            return this.reports.slice(start, end);
        },

        totalPages() {
            return Math.ceil(this.reports.length / this.perPage);
        },

        pages() {
            const range = [];
            const start = Math.max(1, this.currentPage - 2);
            const end = Math.min(this.totalPages, this.currentPage + 2);

            for (let i = start; i <= end; i++) {
                range.push(i);
            }

            return range;
        }
    },

    methods: {
        clearFilters() {
            this.filters = {
                referenceSearch: null,
                courierFilter: [],
                merchantFilter: [],
                reportValueSearch: null,
                noteSearch: null,
                issuedByFilter: [],
                createdAtSearch: null
            };
            this.currentPage = 1;
        },

        selectReport(report, index, event) {
            let updatedSelection = [...this.selectedReports];
            const reportId = report._id;

            if (event.shiftKey && this.lastSelectedIndex !== null) {
                // Shift-click selection logic
                const start = Math.min(index, this.lastSelectedIndex);
                const end = Math.max(index, this.lastSelectedIndex);

                for (let i = start; i <= end; i++) {
                    const s = this.paginatedReports[i];
                    const sId = s._id;
                    if (!updatedSelection.includes(sId)) {
                        updatedSelection.push(sId);
                    }
                }
            } else {
                // Single click selection logic
                const existingIndex = updatedSelection.indexOf(reportId);
                if (existingIndex !== -1) {
                    updatedSelection.splice(existingIndex, 1);
                } else {
                    updatedSelection.push(reportId);
                }
                this.lastSelectedIndex = index;
            }

            // Update local state which will propagate to map
            this.selectedReports = updatedSelection;
        },

        isSelected(report) {
            return this.selectedReports.includes(report._id);
        },

        toggleSelectAll() {
            let newSelection;
            if (this.selectedReports.length === this.reports.length) {
                newSelection = [];
            } else {
                newSelection = this.reports.map(s => s._id);
            }
            this.updateSelectedReports(newSelection);
        },

        deselectAll() {
            this.updateSelectedReports([]);
        },

        updateSelectedReports(updatedSelection) {
            this.selectedReports = updatedSelection;
        },

        async hasPermission(permission) {
            try {
                const response = await axios.get(`/partner-users/check-permission/${permission}`);
                return response.data.hasPermission;
            } catch (error) {
                console.error('Error checking permission:', error);
                return false;
            }
        },

        revertReport(report) {
            Swal.fire({
                title: this.$t('message_revert_report'),
                text: this.$t('message_revert_report_confirmation'),
                icon: 'warning',
                showCancelButton: true,
                confirmButtonText: this.$t('button_yes'),
                cancelButtonText: this.$t('button_no')
            }).then((result) => {
                if (result.isConfirmed) {
                    axios.post(`/reports/revert/${report._id}`)
                        .then(response => {
                            Swal.fire({
                                title: this.$t('message_success'),
                                text: this.$t('message_report_reverted'),
                                icon: 'success',
                                showConfirmButton: false,
                                timer: 1500,
                            });
                            this.$emit('report-reverted', report._id);
                        })
                        .catch(error => {
                            console.error('Error reverting report:', error);
                            Swal.fire({
                                title: this.$t('message_error'),
                                text: this.$t('message_report_revert_failed'),
                                icon: 'error'
                            });
                        });
                }
            });
        },

        updatePerPage() {
            this.currentPage = 1;
        },

        searchReports() {
            this.currentPage = 1;
        },

        prevPage() {
            if (this.currentPage > 1) this.currentPage--;
        },

        nextPage() {
            if (this.currentPage < this.totalPages) this.currentPage++;
        },

        goToPage(page) {
            this.currentPage = page;
        },

        toggleStatusFilter(statusId) {
            const index = this.filters.status.indexOf(statusId);
            if (index === -1) {
                this.filters.status.push(statusId);
            } else {
                this.filters.status.splice(index, 1);
            }
            this.currentPage = 1;
        },

        navigateTo(url) {
            window.location.href = url;
        },

        formatNumber(number) {
            return new Intl.NumberFormat().format(number);
        },

        formatDate(date) {
            return new Date(date).toLocaleString();
        },

        fetchUsers() {
            $.ajax({
                url: '/get-all-clients',  // Replace with your endpoint
                method: 'GET',
                success: (data) => {
                    this.users = data;
                },
                error: (xhr, status, error) => {
                    console.error('Error fetching users:', error);
                }
            });
        },

        statusIconClass(status) {
            return {
                'settled': 'fas fa-check-circle text-green-500',
                'pending': 'fas fa-clock text-yellow-500'
            }[status] || 'fas fa-question-circle text-gray-500'
        },
        statusLabel(status) {
            return {
                'settled': this.$t('status.settled'),
                'pending': this.$t('status.pending')
            }[status] || this.$t('placeholder.any')
        },
    }
}
</script>

<style scoped>

</style>